import { i18n } from '@/commons/utils';
import { disableForm } from './complete-account';
import {
  getStoredIdentifier,
  disableCompanyCheckbox,
  setSmartId,
  setMemberType,
  displayOptions,
} from '../customer-utils';
import { displayMobileValidation } from './complete-account-identifier';

const services = {
  RECOVER_DATA: 'rest/model/atg/userprofiling/ProfileActor/recover-data',
  RESET_PASSWORD_EMAIL: '/rest/model/atg/userprofiling/ProfileActor/email-reset-pwd',
};

export const init = form => {
  const signin = cube.$id('js-sign-in');
  signin.on('click', addParametersBeforeRedirection);

  const recoverData = cube.$id('js-recover-data');
  recoverData.on('click', sendRecoverDataMail);

  const mobileRecoverData = cube.$id('js-recover-data-only-mobile');
  mobileRecoverData.on('click', addParametersBeforeRedirection);

  const resetPassword = cube.$id('js-reset-password');
  resetPassword.on('click', resetPasswordByMail);

  const itsMyMobile = cube.$id('js-it-is-my-mobile');
  itsMyMobile.on('click', event => forceMobileValidation(event, form));

  const continueWithoutLogin = cube.$id('js-continue-without-login');
  continueWithoutLogin.on('click', event => forceValidation(event, form));

  const resetPasswordOnlyMobile = cube.$id('js-reset-password-only-mobile');
  resetPasswordOnlyMobile.on('click', addParametersBeforeRedirection);

  initSecondIdentifierOptionBlock(form);
};

export const initSecondIdentifierOptionBlock = form => {
  if (cube.$id('js-second-identifier-option-block')) {
    const signinSi = cube.$id('js-sign-in-si');
    signinSi.on('click', event => addParametersBeforeRedirection(event, 'SECOND'));

    const recoverDataSi = cube.$id('js-recover-data-si');
    recoverDataSi.on('click', event => sendRecoverDataMail(event, 'SECOND'));

    const mobileRecoverDataSi = cube.$id('js-recover-data-only-mobile-si');
    mobileRecoverDataSi.on('click', event => addParametersBeforeRedirection(event, 'SECOND'));

    const resetPasswordSi = cube.$id('js-reset-password-si');
    resetPasswordSi.on('click', event => resetPasswordByMail(event, 'SECOND'));

    const itsMyMobileSi = cube.$id('js-it-is-my-mobile-si');
    itsMyMobileSi.on('click', event => forceMobileValidation(event, form, 'SECOND'));

    const continueWithoutLoginSi = cube.$id('js-continue-without-login-si');
    continueWithoutLoginSi.on('click', event => forceValidation(event, form, 'SECOND'));

    const resetPasswordOnlyMobileSi = cube.$id('js-reset-password-only-mobile-si');
    resetPasswordOnlyMobileSi.on('click', event => addParametersBeforeRedirection(event, 'SECOND'));
  }
};

/**
 * Send an XHR to send a mail to the customer with a recover data link.
 *
 * @param {Event} event
 * @param {String} identifierType 'MAIN' or 'SECOND' identifier.
 */
const sendRecoverDataMail = (event, identifierType = 'MAIN') => {
  event.preventDefault();

  const link = event.target;

  if (!link.classList.contains('disabled')) {
    link.classList.add('disabled');

    cube.ajax
      .post(services.RECOVER_DATA, getStoredIdentifier(identifierType))
      .onSuccess(response => sendRecoverDataMailSuccess(response, link))
      .onError(error)
      .call();
  }
};

/**
 * Display a message if the XHR returns no error(s).
 *
 * @param {Object} response The XHR response.
 * @param {HTMLAnchorElement} link The clicked anchor.
 */
const sendRecoverDataMailSuccess = (response, link) => {
  if (error(response, link)) return;

  link.classList.remove('disabled');
  cube.alert.success(i18n('createAccount.completeAccountEmailSuccessMessage'));
};

/**
 * Add customer's informations to the link before redirection.
 *
 * @param {Event} event
 * @param {String} identifierType 'MAIN' or 'SECOND' identifier.
 */
const addParametersBeforeRedirection = (event, identifierType = 'MAIN') => {
  event.preventDefault();

  const link = event.target;
  const { login, countryCode } = getStoredIdentifier(identifierType);

  window.location = `${link.href}${link.href.includes('?') ? '&' : '?'}login=${encodeURIComponent(login)}${
    countryCode ? `&countryCode=${countryCode}` : ''
  }`;
};

/**
 * Send an XHR to send a mail with a recover password link.
 *
 * @param {Event} event
 * @param {String} identifierType 'MAIN' or 'SECOND' identifier.
 */
const resetPasswordByMail = (event, identifierType = 'MAIN') => {
  event.preventDefault();

  const link = event.target;

  if (!link.classList.contains('disabled')) {
    link.classList.add('disabled');

    cube.ajax
      .post(services.RESET_PASSWORD_EMAIL, getStoredIdentifier(identifierType))
      .onSuccess(response => resetPasswordSuccess(response, link))
      .onError(error)
      .call();
  }
};

/**
 * Display a message if the XHR returns no error(s).
 *
 * @param {Object} response The XHR response.
 * @param {HTMLAnchorElement} link The clicked anchor.
 */
const resetPasswordSuccess = (response, link) => {
  if (error(response, link)) return;

  link.classList.remove('disabled');
  cube.alert.success(i18n('account.lostpassword.successMsg'));
};

/**
 * Display an error message if the XHR returns error.
 *
 * @param {Object} response The XHR response.
 * @param {HTMLAnchorElement} link The clicked anchor.
 */
const error = (response, link) => {
  if (
    typeof response === 'string' ||
    (typeof response === 'object' &&
      response.responseTO &&
      response.responseTO.errors[0] &&
      response.responseTO.errors[0].message)
  ) {
    if (typeof response === 'string') cube.alert.error(i18n('error.oc.call'));
    else cube.alert.error(response.responseTO.errors[0].message);

    link.classList.remove('disabled');

    return true;
  }

  return false;
};

/**
 * Force the mobile validation if the customer has already an accout with this number.
 *
 * @param {Event} event
 * @param {HTMLFormElement} form The create account form.
 * @param {String} identifierType 'MAIN' or 'SECOND' identifier.
 */
const forceMobileValidation = (event, form, identifierType = 'MAIN') => {
  event.preventDefault();

  const data = getStoredIdentifier(identifierType);

  disableForm(form, false);
  displayOptions(false, identifierType);
  displayMobileValidation(true, data, identifierType);
};

/**
 * Force the mobile validation if the customer has already used his number in another account.
 *
 * @param {Event} event
 * @param {HTMLFormElement} form The create account form.
 * @param {String} identifierType 'MAIN' or 'SECOND' identifier.
 */
const forceValidation = (event, form, identifierType = 'MAIN') => {
  event.preventDefault();

  const data = getStoredIdentifier(identifierType);

  setSmartId(data.personId);
  setMemberType(data.memberType);
  displayOptions(false, identifierType);

  if (identifierType === 'MAIN') {
    disableForm(form, false);
    disableCompanyCheckbox(true);
  }

  if (data.identifierType === 'MOBILE') displayMobileValidation(true, data, identifierType);
};



// WEBPACK FOOTER //
// ./src/customer/complete-account/complete-account-options.js